import type { AITranslatorSettings } from "./constants/translator-defaults"

export type TranslatorRequest = {
  use_translator: true
  target_languages: string[]
  custom_translation_instructions: string
  translation_dictionaries: Record<string, Record<string, string>>
}

export type TranslatorToolControl = {
  enabled: boolean
  availableLanguages: string[]
  selectedLanguages: string[]
  glossaryLanguage: string
  onEnabledChange: (enabled: boolean) => void
  onLanguageChange: (language: string, checked: boolean) => void
  onAddLanguage: (language: string) => string | undefined
  onAdvanced: () => void
  onHistory: () => void
}

export function selectedTranslationLanguages(settings: AITranslatorSettings): string[] {
  const languages = settings.selectedLanguages ?? [settings.selectedLanguage]
  const selected = [
    ...new Set(languages.filter((language) => settings.availableLanguages.includes(language))),
  ].slice(0, 10)
  return selected.length ? selected : settings.availableLanguages.slice(0, 1)
}

export function buildTranslatorRequest(settings: AITranslatorSettings): TranslatorRequest {
  const languages = selectedTranslationLanguages(settings)
  if (languages.length === 0) throw new Error("Select at least one target language.")
  const glossary = Object.fromEntries(
    settings.customTranslations.map(({ original, translation }) => [original, translation])
  )
  return {
    use_translator: true,
    target_languages: languages,
    custom_translation_instructions: settings.translationInstructions,
    // The historical glossary was authored for one language. Never inject its
    // values into another language just because multiple targets are selected.
    translation_dictionaries: { [settings.selectedLanguage]: glossary },
  }
}

export function translatorOverridesForSource(
  request: TranslatorRequest | null,
  {
    fileCount,
    documentCount,
    sessionFiles = [],
    indexedFileCount = 0,
  }: {
    fileCount: number
    documentCount: number
    sessionFiles?: string[]
    indexedFileCount?: number
  }
) {
  if (!request) return {}
  let sourceKind = "text"
  if (documentCount > 0) sourceKind = "documents"
  if (fileCount > 0) sourceKind = "files"
  const storedFiles = fileCount === 0 && documentCount === 0 ? sessionFiles : []
  if (storedFiles.length > 0 || (fileCount === 0 && documentCount === 0 && indexedFileCount > 0)) {
    sourceKind = "files"
  }
  return {
    ...request,
    translation_source_kind: sourceKind,
    ...(storedFiles.length > 0 ? { translation_session_files: storedFiles } : {}),
    use_agent_mode: false,
    start_research: false,
    suggest_followup_questions: false,
  }
}
